import type {
    CarouselKeyStepConstantType,
    CarouselMetricsConstantType,
    CarouselPerViewConstantType,
    CarouselSettleConstantType,
    CarouselOffsetConstantType,
    CarouselKeyboardEventType,
    CarouselPointerEventType,
    CarouselWebHandlersType,
    CarouselEventTargetType,
    CarouselFocusEventType,
    CarouselMetrics
} from "./type";
import {
    type HorizontalStepType,
    getHorizontalStep
} from "../../helpers/direction";

export const CAROUSEL_VERTICAL_STEPS: Record<string, HorizontalStepType> = {
    ArrowDown: 1,
    ArrowUp: -1
};

export const CAROUSEL_EDITABLE_TAGS: Array<string> = [
    "TEXTAREA",
    "SELECT",
    "INPUT"
];

export const clampCarouselIndex = ({
    pageCount,
    index
}: {
    pageCount: number;
    index: number;
}): number => {
    return Math.max(0, Math.min(index, pageCount - 1));
};

export const getCarouselPerView = ({
    shapeConfigs,
    viewportSize,
    visibleCount,
    orientation,
    gap
}: CarouselPerViewConstantType): number => {
    if(orientation === "vertical") {
        return visibleCount;
    }

    const minItemSize = shapeConfigs.compactBreakpoint / 4;

    return Math.max(1, Math.min(visibleCount, Math.floor((viewportSize + gap) / (minItemSize + gap))));
};

export const getCarouselMetrics = ({
    viewportSize,
    perView,
    count,
    peek,
    gap
}: CarouselMetricsConstantType): CarouselMetrics => {
    const itemSize = Math.max(0, (viewportSize - (perView - 1) * gap - peek) / perView);

    const totalSize = count * itemSize + Math.max(0, count - 1) * gap;

    return {
        maxOffset: Math.max(0, totalSize - viewportSize),
        pageCount: Math.max(1, count - perView + 1),
        step: itemSize + gap,
        totalSize,
        itemSize
    };
};

export const getCarouselOffset = ({
    metrics,
    index
}: CarouselOffsetConstantType): number => {
    return Math.max(0, Math.min(index * metrics.step, metrics.maxOffset));
};

export const getCarouselSettleIndex = ({
    velocity,
    metrics,
    offset,
    coast
}: CarouselSettleConstantType): number => {
    if(metrics.step <= 0) {
        return 0;
    }

    return clampCarouselIndex({
        index: Math.round((offset + velocity * coast) / metrics.step),
        pageCount: metrics.pageCount
    });
};

export const getCarouselKeyStep = ({
    orientation,
    direction,
    key
}: CarouselKeyStepConstantType): HorizontalStepType => {
    if(orientation === "vertical") {
        return CAROUSEL_VERTICAL_STEPS[key] ?? 0;
    }

    return getHorizontalStep({
        direction,
        key
    });
};

export const isCarouselNodeInside = ({
    container,
    node
}: {
    container: unknown;
    node: unknown;
}): boolean => {
    if(!container || !node || typeof container !== "object" || !("contains" in container) || typeof container.contains !== "function") {
        return false;
    }

    return !!container.contains(node);
};

export const isCarouselEditableTarget = ({
    target
}: {
    target: unknown;
}): boolean => {
    if(!target || typeof target !== "object") {
        return false;
    }

    if("isContentEditable" in target && target.isContentEditable === true) {
        return true;
    }

    return "tagName" in target && typeof target.tagName === "string" && CAROUSEL_EDITABLE_TAGS.includes(target.tagName);
};

export const isCarouselTabTarget = ({
    target
}: {
    target: unknown;
}): boolean => {
    if(!target || typeof target !== "object" || !("getAttribute" in target) || typeof target.getAttribute !== "function") {
        return false;
    }

    return target.getAttribute("role") === "tab";
};

export const setCarouselNodeInert = ({
    isInert,
    node
}: {
    isInert: boolean;
    node: unknown;
}) => {
    if(!node || typeof node !== "object" || !("toggleAttribute" in node) || typeof node.toggleAttribute !== "function") {
        return;
    }

    node.toggleAttribute("inert", isInert);
};

const isCarouselEventTarget = (node: unknown): node is CarouselEventTargetType => {
    return !!node && typeof node === "object" && "addEventListener" in node && typeof node.addEventListener === "function" && "removeEventListener" in node && typeof node.removeEventListener === "function";
};

const isCarouselKeyboardEvent = (event: unknown): event is CarouselKeyboardEventType => {
    return !!event && typeof event === "object" && "key" in event && typeof event.key === "string" && "preventDefault" in event && typeof event.preventDefault === "function";
};

export const subscribeCarouselWebEvents = ({
    handlers,
    node
}: {
    handlers: {
        current: CarouselWebHandlersType;
    };
    node: unknown;
}): (() => void) => {
    if(!isCarouselEventTarget(node)) {
        return () => {};
    }

    const listeners: Record<string, (event: unknown) => void> = {
        pointerenter: (event) => handlers.current.onPointerEnter(event as CarouselPointerEventType),
        focusin: (event) => handlers.current.onFocus(event as CarouselFocusEventType),
        focusout: (event) => handlers.current.onBlur(event as CarouselFocusEventType),
        pointerleave: () => handlers.current.onPointerLeave(),
        keydown: (event) => {
            if(isCarouselKeyboardEvent(event)) {
                handlers.current.onKeyDown(event);
            }
        }
    };

    Object.entries(listeners).forEach(([
        name,
        listener
    ]) => node.addEventListener(name, listener));

    return () => {
        Object.entries(listeners).forEach(([
            name,
            listener
        ]) => node.removeEventListener(name, listener));
    };
};
